import React from 'react'
import { Autocomplete, FormControlLabel, TextField, CircularProgress } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'

const ItemFormAutoComplete = ({
    name,
    labelKey,
    rules,
    options = [],
    required = false,
    multiple = false,
    placeholder = '',
    noOptionsText = 'No options',
    loading = false,
    onSearchChange,
    onScrollToBottom,
    ...props
}) => {
    const { t } = useTranslation()
    const { control, formState: { errors } } = useFormContext()
    const { labelPlacement = "start", onChangeCustom, disabled, readOnly = false } = props

    const error = get(errors, name)

    const handleScroll = (event) => {
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1) {
            if (onScrollToBottom) {
                onScrollToBottom();
            }
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { onChange, value, ref, ...field } }) => (
                <FormControlLabel
                    label={t(labelKey)}
                    labelPlacement={labelPlacement}
                    required={required}
                    control={
                        <Autocomplete
                            {...field}
                            multiple={multiple}
                            disabled={disabled}
                            readOnly={readOnly}
                            options={options}
                            getOptionLabel={(option) => option.label || ''}
                            isOptionEqualToValue={(option, val) => option.value === val.value}
                            value={
                                multiple
                                    ? options.filter((op) => (value || []).includes(op.value))
                                    : options.find((op) => op.value === value) || null
                            }
                            onChange={(event, newValue) => {
                                if (readOnly) return;
                                onChange(
                                    multiple
                                        ? newValue.map((item) => item.value)
                                        : newValue ? newValue.value : ''
                                )
                                if (onChangeCustom) {
                                    onChangeCustom(newValue);
                                }
                            }}
                            loading={loading}
                            onInputChange={(event, value, reason) => {
                                if (readOnly) return;
                                if (reason === 'input' && onSearchChange) {
                                    onSearchChange(value);
                                }
                            }}
                            ListboxProps={{
                                onScroll: handleScroll
                            }}
                            noOptionsText={t(noOptionsText)}
                            fullWidth
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    inputRef={ref}
                                    error={!!error}
                                    helperText={error?.message ? t(error.message) : ''}
                                    placeholder={placeholder ? t(placeholder) : ''}
                                    sx={{ '& .MuiOutlinedInput-root': { borderColor: '#8F9294' } }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />
                            )}
                        />
                    }
                />
            )}
        />
    )
}

export default ItemFormAutoComplete
